import { mat4 } from '../math/mat4'
import { quat, type Quat } from '../math/quat'
import { vec3, type Vec3 } from '../math/vec3'
import { pose as poseOps, type Pose } from '../math/pose'
import { snapToRay, type CameraModel } from '../camera/model'
import type { FaceFrame } from '../tracking/types'
import type { AnchorSpec, FaceAnchorOrigin } from '../product/schema'
import { FACE_ANCHORS } from './face-anchors'

export interface FaceAnchorInput {
  frame: FaceFrame
  camera: CameraModel
  spec: Pick<AnchorSpec, 'origin' | 'offset' | 'rotation' | 'scale' | 'refineLandmark'>
  /** Snap the anchor's screen position to the detected landmark (default true). */
  refine2D?: boolean
}

export interface FaceAnchorResult {
  /** Anchor pose in camera space (cm). */
  pose: Pose
  /** Anchor pose in head (canonical) space — useful for procedural geometry. */
  local: Pose
  /** Head pose (rotation + translation of the canonical frame) in camera space. */
  head: Pose
}

const _rot: Quat = [0, 0, 0, 1]
const _v: Vec3 = [0, 0, 0]

/**
 * Generic face-anchored solver: pose = head ∘ (originPoint + offset, rotation, scale),
 * then optionally re-projected so the anchor sits exactly on its 2-D landmark.
 * Pure function of the frame → deterministic, portable, testable.
 */
export function solveFaceAnchor(input: FaceAnchorInput, out?: FaceAnchorResult): FaceAnchorResult {
  const res: FaceAnchorResult = out ?? { pose: poseOps.create(), local: poseOps.create(), head: poseOps.create() }
  const { frame, camera, spec } = input
  const def = FACE_ANCHORS[spec.origin as FaceAnchorOrigin] ?? FACE_ANCHORS.head_center

  // head pose from the matrix
  poseOps.fromMat4(res.head, frame.headMatrix)
  res.head.scale = 1

  // local anchor pose (canonical space)
  vec3.add(res.local.position, def.point, spec.offset)
  quat.fromEulerDeg(res.local.rotation, spec.rotation[0], spec.rotation[1], spec.rotation[2])
  if (def.rotation) {
    quat.fromEulerDeg(_rot, def.rotation[0], def.rotation[1], def.rotation[2])
    quat.multiply(res.local.rotation, _rot, res.local.rotation)
  }
  res.local.scale = spec.scale

  // camera-space pose = head ∘ local  (use the full matrix so any scale in it is respected)
  mat4.transformPoint(res.pose.position, frame.headMatrix, res.local.position)
  quat.multiply(res.pose.rotation, res.head.rotation, res.local.rotation)
  quat.normalize(res.pose.rotation, res.pose.rotation)
  res.pose.scale = spec.scale

  // 2-D refinement: keep depth from the head matrix, take x/y from the observed landmark ray.
  const refine = input.refine2D ?? true
  const lmIndex = spec.refineLandmark ?? def.landmark
  if (refine && lmIndex !== null && lmIndex !== undefined && lmIndex * 3 + 2 < frame.landmarks.length) {
    // The landmark corresponds to def.point (without the user offset). Snap that point, then re-apply the
    // offset in head space so fitting sliders still behave as pure local translations.
    mat4.transformPoint(_v, frame.headMatrix, def.point)
    snapToRay(_v, camera, _v, frame.landmarks[lmIndex * 3]!, frame.landmarks[lmIndex * 3 + 1]!)
    // add rotated offset
    mat4.transformDirection(res.pose.position, frame.headMatrix, spec.offset)
    vec3.add(res.pose.position, res.pose.position, _v)
  }
  return res
}
